import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import useSubmitGuard from '../hooks/useSubmitGuard';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Plus, RefreshCw, Trash2, Scale, Package, CircleOff, Search } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import DeleteRequestDialog from '../components/DeleteRequestDialog';
import PendingDeletionsPanel from '../components/PendingDeletionsPanel';
import SearchableSelect from '../components/SearchableSelect';
import { 
  FilterBar, 
  SearchFilter, 
  SelectFilter, 
  DateRangeFilter, 
  countActiveFilters, 
  filterByDateRange 
} from '../components/FilterComponents';
import { ExportButton } from '../utils/exportUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ITEM_TYPES = [
  { value: 'tyre', label: 'Tyre' },
  { value: 'machine_part', label: 'Machine Part' },
  { value: 'other', label: 'Other' },
];
const TYPE_COLORS = { tyre: '#ef4444', machine_part: '#f59e0b', other: '#6b7280' };
const TYPE_LABELS = { tyre: 'Tyres', machine_part: 'Machine Parts', other: 'Other' };

function ScrapPage({ user }) {
  const canApprove = user?.is_admin || user?.is_super_admin;
  const guard = useSubmitGuard();

  // Delete approval
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pendingDelRequests, setPendingDelRequests] = useState([]);

  const fetchPendingDeletions = async () => {
    if (!canApprove) return;
    try {
      const t = localStorage.getItem('token');
      const res = await axios.get(`${API}/delete-requests?status=pending&module=scrap`, {
        headers: { Authorization: `Bearer ${t}` }
      });
      setPendingDelRequests(res.data);
    } catch {}
  };

  const handleApproveDelete = async (reqId) => {
    try {
      const t = localStorage.getItem('token');
      await axios.post(`${API}/delete-requests/${reqId}/approve`, {}, { headers: { Authorization: `Bearer ${t}` } });
      toast.success('Deletion approved');
      fetchAll();
      fetchPendingDeletions();
    } catch { toast.error('Failed to approve'); }
  };

  const handleRejectDelete = async (reqId) => {
    try {
      const t = localStorage.getItem('token');
      await axios.post(`${API}/delete-requests/${reqId}/reject`, { note: 'Rejected' }, { headers: { Authorization: `Bearer ${t}` } });
      toast.success('Request rejected');
      fetchPendingDeletions();
    } catch { toast.error('Failed to reject'); }
  };

  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({ total_entries: 0, total_weight_kg: 0, by_type: {} });
  const [tyres, setTyres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [scrapFilters, setScrapFilters] = useState({ search: '', dateRange: { from: null, to: null } });
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    item_type: 'tyre',
    description: '',
    serial_number: '',
    tyre_id: 'none',
    quantity: 1,
    weight_kg: '',
    condition_notes: '',
    scrapped_date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchAll(); fetchPendingDeletions(); }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [entriesRes, statsRes, tyresRes] = await Promise.all([
        axios.get(`${API}/scrap`, { headers }),
        axios.get(`${API}/scrap/stats`, { headers }),
        axios.get(`${API}/tyres?status=in_stock`, { headers }),
      ]);
      setEntries(entriesRes.data);
      setStats(statsRes.data);
      setTyres(tyresRes.data);
    } catch {
      toast.error('Failed to load scrap data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!form.description || !form.scrapped_date) {
      toast.error('Please fill in description and date');
      return;
    }
    guard(async () => {
      try {
        const payload = {
          ...form,
          weight_kg: form.weight_kg ? parseFloat(form.weight_kg) : null,
          quantity: parseFloat(form.quantity) || 1,
          tyre_id: form.item_type === 'tyre' && form.tyre_id && form.tyre_id !== 'none' ? form.tyre_id : null,
        };
        await axios.post(`${API}/scrap`, payload, { headers });
        toast.success('Scrap entry logged');
        setShowAdd(false);
        setForm({
          item_type: 'tyre', description: '', serial_number: '', tyre_id: 'none',
          quantity: 1, weight_kg: '', condition_notes: '',
          scrapped_date: new Date().toISOString().split('T')[0], notes: '',
        });
        fetchAll();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to log scrap');
      }
    });
  };

  const handleDelete = async (entry) => {
    if (canApprove) {
      if (!window.confirm('Delete this scrap entry?')) return;
      try {
        await axios.delete(`${API}/scrap/${entry.id}`, { headers });
        toast.success('Entry deleted');
        fetchAll();
        fetchPendingDeletions();
      } catch { toast.error('Failed to delete'); }
    } else {
      setDeleteTarget({ id: entry.id, display: `Scrap – ${entry.description} (${entry.scrapped_date})` });
    }
  };

  const pieData = Object.entries(stats.by_type || {}).map(([k, v]) => ({
    name: TYPE_LABELS[k] || k,
    value: v,
    color: TYPE_COLORS[k] || '#94a3b8',
  }));

  const filtered = useMemo(() => {
    let result = filterType === 'all' ? entries : entries.filter(e => e.item_type === filterType);
    if (scrapFilters.search) {
      const q = scrapFilters.search.toLowerCase();
      result = result.filter(e =>
        e.description?.toLowerCase().includes(q) ||
        e.serial_number?.toLowerCase().includes(q) ||
        e.logged_by?.toLowerCase().includes(q)
      );
    }
    result = filterByDateRange(result, 'scrapped_date', scrapFilters.dateRange);
    return result;
  }, [entries, filterType, scrapFilters]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Scrap Management</h1>
          <p className="text-slate-500 text-sm mt-1">Log all scrap items entering the scrap yard</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAll}>
            <RefreshCw className="h-4 w-4 mr-2" />Refresh
          </Button>
          <Button onClick={() => setShowAdd(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="add-scrap-btn">
            <Plus className="h-4 w-4 mr-2" />Log Scrap
          </Button>
        </div>
      </div>

      {/* Stats */}
      <PendingDeletionsPanel module="scrap" requests={pendingDelRequests} onApprove={handleApproveDelete} onReject={handleRejectDelete} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 flex items-center gap-4">
            <CircleOff className="h-8 w-8 text-red-400" />
            <div>
              <p className="text-xs text-slate-500">Total Entries</p>
              <p className="text-2xl font-bold text-slate-800">{stats.total_entries}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 flex items-center gap-4">
            <Scale className="h-8 w-8 text-slate-400" />
            <div>
              <p className="text-xs text-slate-500">Total Weight</p>
              <p className="text-2xl font-bold text-slate-800">{stats.total_weight_kg?.toFixed(1) || 0} kg</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 flex items-center gap-4">
            <Package className="h-8 w-8 text-amber-400" />
            <div>
              <p className="text-xs text-slate-500">Scrapped Tyres</p>
              <p className="text-2xl font-bold text-slate-800">{stats.by_type?.tyre || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Pie Chart */}
        {pieData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Scrap by Category</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label>
                    {pieData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="md:col-span-2 space-y-3">
          <FilterBar
            onReset={() => setScrapFilters({ search: '', dateRange: { from: null, to: null } })}
            activeFiltersCount={countActiveFilters(scrapFilters)}
          >
            <SearchFilter
              label="Search"
              value={scrapFilters.search}
              onChange={(val) => setScrapFilters(p => ({ ...p, search: val }))}
              placeholder="Description, serial, logged by..."
            />
            <DateRangeFilter
              label="Scrapped Date"
              value={scrapFilters.dateRange}
              onChange={(val) => setScrapFilters(p => ({ ...p, dateRange: val }))}
            />
          </FilterBar>
          {/* Type Filter */}
          <div className="flex gap-2 flex-wrap">
            {['all', ...ITEM_TYPES.map(t => t.value)].map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  filterType === type
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`scrap-filter-${type}`}
              >
                {type === 'all' ? 'All' : TYPE_LABELS[type] || type}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-500">{filtered.length} entries</span>
            <ExportButton
              rows={filtered}
              columns={[
                { header: 'Type', key: r => TYPE_LABELS[r.item_type] || r.item_type },
                { header: 'Description', key: 'description' },
                { header: 'Serial #', key: 'serial_number' },
                { header: 'Qty', key: 'quantity' },
                { header: 'Weight (kg)', key: 'weight_kg' },
                { header: 'Date', key: 'scrapped_date' },
                { header: 'Logged By', key: 'logged_by' },
                { header: 'Condition Notes', key: 'condition_notes' },
              ]}
              filename={`scrap_log_${new Date().toISOString().slice(0,10)}`}
              title="Scrap Yard Log"
              subtitle={`Exported on ${new Date().toLocaleDateString('en-IN')}`}
            />
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Serial #</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Weight</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Logged By</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow>
                  ) : filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">No scrap entries. Use "Log Scrap" to add items.</TableCell></TableRow>
                  ) : filtered.map(e => (
                    <TableRow key={e.id}>
                      <TableCell>
                        <Badge
                          className="text-xs"
                          style={{ backgroundColor: `${TYPE_COLORS[e.item_type]}20`, color: TYPE_COLORS[e.item_type] }}
                        >
                          {TYPE_LABELS[e.item_type] || e.item_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {e.description}
                        {e.condition_notes && <div className="text-xs text-slate-500">{e.condition_notes}</div>}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{e.serial_number || '—'}</TableCell>
                      <TableCell className="text-right">{e.quantity}</TableCell>
                      <TableCell className="text-right">{e.weight_kg != null ? `${e.weight_kg} kg` : '—'}</TableCell>
                      <TableCell className="text-sm">{e.scrapped_date}</TableCell>
                      <TableCell className="text-sm text-slate-500">{e.logged_by}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(e)} className="text-red-500 hover:text-red-700" data-testid={`delete-scrap-${e.id}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ===== ADD SCRAP MODAL ===== */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Log Scrap Entry</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Item Type *</Label>
                <Select value={form.item_type} onValueChange={v => setForm(p => ({ ...p, item_type: v, tyre_id: 'none' }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ITEM_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Scrapped Date *</Label>
                <Input type="date" value={form.scrapped_date} onChange={e => setForm(p => ({ ...p, scrapped_date: e.target.value }))}
                  min={!user?.is_super_admin ? new Date().toISOString().split('T')[0] : undefined}
                  max={!user?.is_super_admin ? new Date().toISOString().split('T')[0] : undefined}
                />
                {!user?.is_super_admin && <p className="text-xs text-amber-600 mt-1">Only today's date allowed</p>}
              </div>
            </div>

            {form.item_type === 'tyre' && (
              <div className="space-y-1">
                <Label>Link to Tyre Record (optional)</Label>
                <SearchableSelect
                  value={form.tyre_id}
                  onValueChange={v => {
                    const t = tyres.find(x => x.id === v);
                    setForm(p => ({
                      ...p, tyre_id: v,
                      serial_number: t?.serial_number || p.serial_number,
                      description: t ? `${t.brand} ${t.size} - ${t.serial_number}` : p.description,
                    }));
                  }}
                  options={[
                    { value: 'none', label: 'Not linked' },
                    ...tyres.map(t => ({ value: t.id, label: `${t.serial_number} — ${t.brand} ${t.size} (${t.total_hours_used?.toFixed(0)}h)` }))
                  ]}
                  placeholder="Select tyre (in stock)"
                />
              </div>
            )}

            <div className="space-y-1">
              <Label>Description *</Label>
              <Input placeholder="e.g. Worn Michelin 315/80R22.5 tyre" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} data-testid="scrap-description-input" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Serial Number (optional)</Label>
                <Input placeholder="Serial / part number" value={form.serial_number} onChange={e => setForm(p => ({ ...p, serial_number: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Quantity</Label>
                <Input type="number" min="1" step="0.5" value={form.quantity} onChange={e => setForm(p => ({ ...p, quantity: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Weight (kg, optional)</Label>
                <Input type="number" min="0" step="0.1" placeholder="0.0" value={form.weight_kg} onChange={e => setForm(p => ({ ...p, weight_kg: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Condition Notes</Label>
                <Input placeholder="Condition when scrapped" value={form.condition_notes} onChange={e => setForm(p => ({ ...p, condition_notes: e.target.value }))} />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Notes</Label>
              <Input placeholder="Any additional notes" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleAdd} className="bg-slate-900 hover:bg-slate-800" data-testid="scrap-submit-btn">Log Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteRequestDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        module="scrap"
        entryId={deleteTarget?.id}
        entryDisplay={deleteTarget?.display}
        onSuccess={() => setDeleteTarget(null)}
      />
    </div>
  );
}

export default ScrapPage;
